import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Heart, Users, Award, MapPin } from "lucide-react";

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917] transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          <a href="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </a>
          <Button
            onClick={() => navigate("/booking")}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white text-xs uppercase tracking-widest"
          >
            Book Now
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_bookmate-32/artifacts/mnrnnulb_20200713182810432397000000-o.jpg"
            alt="Chestnut Hill Inn"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative h-full flex flex-col justify-center items-center text-center px-6">
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-4">
            About Us
          </h1>
          <p className="text-white/80 text-lg max-w-xl">
            The story behind Chestnut Hill Inn
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#D97706] uppercase tracking-[0.3em] text-xs font-semibold mb-4">
              Est. 1900
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6">
              Our Story
            </h2>
            <div className="w-16 h-px bg-[#134E4A] mx-auto" />
          </div>
          
          <div className="prose prose-lg mx-auto text-[#78716C]">
            <p className="leading-relaxed mb-6">
              Chestnut Hill Inn began its journey over a century ago as a charming coastal cottage 
              on the peaceful shores of Long Island, Maine. Nestled in the heart of Casco Bay, 
              this historic property has welcomed generations of visitors seeking the authentic 
              Maine island experience.
            </p>
            <p className="leading-relaxed mb-6">
              We've lovingly restored and renovated the inn to blend its original 1900s character 
              with modern comforts. From the wrap-around porch with its classic rocking chairs to 
              the beautifully appointed bedrooms with their period details, every corner of 
              Chestnut Hill tells a story.
            </p>
            <p className="leading-relaxed">
              As Superhosts with consistent 5-star reviews, we take pride in offering exceptional 
              hospitality. Whether you're planning a wedding celebration, family reunion, corporate 
              retreat, or girlfriend getaway, we're here to help create lasting memories.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-6 bg-[#F5F5F4]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl text-[#1C1917] text-center mb-12">
            What We Value
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Hospitality",
                desc: "We treat every guest like family, going above and beyond to ensure your stay is perfect."
              },
              {
                icon: Award,
                title: "Excellence",
                desc: "Our Superhost status reflects our commitment to outstanding cleanliness and service."
              },
              {
                icon: Users,
                title: "Community",
                desc: "We love connecting guests with the unique charm of Long Island and its welcoming community."
              }
            ].map((value, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#134E4A] text-white mx-auto mb-6 flex items-center justify-center">
                  <value.icon size={28} />
                </div>
                <h3 className="font-serif text-xl text-[#1C1917] mb-3">{value.title}</h3>
                <p className="text-[#78716C]">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Island */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6">
              Long Island, Maine
            </h2>
            <div className="w-16 h-px bg-[#134E4A] mx-auto" />
          </div>
          
          <div className="prose prose-lg mx-auto text-[#78716C]">
            <p className="leading-relaxed mb-6">
              Long Island is one of the most beautiful and accessible islands in Casco Bay. 
              Just a scenic 45-minute ferry ride from Portland, you'll find yourself in a 
              world apart—quiet beaches, winding roads perfect for biking, and a tight-knit 
              community that welcomes visitors with open arms.
            </p>
            <p className="leading-relaxed mb-6">
              Our inn sits on the west end of the island, just a short walk from three of 
              Maine's most beautiful quiet beaches: South Beach, Fowler Beach, and Wreck Cove. 
              The Bakehouse, Boathouse, and other island gems are all within easy reach.
            </p>
            <p className="leading-relaxed">
              Whether you choose to explore by golf cart, bike, or on foot, you'll discover 
              why Long Island has captured the hearts of so many visitors over the years.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#134E4A]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-white mb-4">
            Come Stay With Us
          </h2>
          <p className="text-white/70 mb-8">
            Experience the magic of Chestnut Hill Inn for yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/booking")}
              className="bg-white text-[#134E4A] hover:bg-white/90 px-8 py-6 text-xs uppercase tracking-widest"
            >
              Book Your Stay
            </Button>
            <Button
              onClick={() => navigate("/reviews")}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#134E4A] px-8 py-6 text-xs uppercase tracking-widest"
            >
              Read Reviews
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-stone-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-serif text-xl text-[#1C1917] mb-2">Chestnut Hill Inn</p>
          <p className="text-[#78716C] text-sm flex items-center justify-center gap-2">
            <MapPin size={14} />
            209 Beach Ave, Long Island, Maine
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <a
              href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/chestnuthillinn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
          <p className="text-[#78716C] text-sm mt-4">
            © {new Date().getFullYear()} Chestnut Hill Inn. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
